/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { Monitor, Moon, Sun } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useTheme, type ThemePref } from './theme';

/**
 * System / Light / Dark, as a segmented control.
 *
 * **Why it lives in the account menu and not on the bar.** The two-row topbar
 * was built by measuring: row one is the product and the person, row two is the
 * board, and the second row exists at all because the first one could not pay
 * for everything competing on it. A theme is about you rather than the board, so
 * row one is the right *row* — but three states need three labels, and "System"
 * cannot be an icon anybody recognises. A single button that cycles through
 * three states is worse: you press it and cannot predict what you get.
 *
 * The menu also gives it something the bar cannot: the surface it is sitting on
 * is itself themed, so choosing dark repaints the menu under your cursor. You
 * see the answer without having to close anything.
 *
 * **One control, not two.** A quick toggle on the bar *and* this would be two
 * controls for one piece of state — the same mistake as the duplicate "Clear
 * all" that had to be taken out of the filter row.
 */
const OPTIONS: { pref: ThemePref; label: string; Icon: typeof Sun }[] = [
  { pref: 'system', label: 'System', Icon: Monitor },
  { pref: 'light', label: 'Light', Icon: Sun },
  { pref: 'dark', label: 'Dark', Icon: Moon },
];

export function ThemeSwitch() {
  const { pref, setPref } = useTheme();

  return (
    <div className="theme-switch mt-1 border-t border-border px-2.5 pb-1 pt-2">
      <p className="m-0 mb-1.5 text-2xs font-semibold uppercase tracking-[0.05em] text-ink-faint">
        Appearance
      </p>

      {/* A radiogroup rather than three buttons: it is one setting with three
          answers, and that is the difference between a screen reader saying
          "Dark, radio button, 3 of 3, selected" and it saying "Dark, button". */}
      <div
        role="radiogroup"
        aria-label="Appearance"
        className="flex rounded-lg border border-border bg-bg p-[3px]"
      >
        {OPTIONS.map(({ pref: value, label, Icon }) => {
          const on = pref === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={on}
              data-theme-pref={value}
              onClick={() => setPref(value)}
              className={cn(
                'tap flex flex-1 items-center justify-center gap-1 rounded-md px-1.5 py-1.5 text-2xs font-semibold transition-colors duration-fast ease-out-soft',
                // `bg-ink text-bg`, which inverts on its own: in light that is a
                // dark chip with a pale label, in dark a pale chip with a dark
                // one. `text-white` here would be invisible on the dark theme's
                // ink, which is a light colour — the same trap the vertical
                // chips fell into.
                on ? 'bg-ink text-bg' : 'text-ink-muted hover:text-ink',
              )}
            >
              <Icon className="size-3.5 shrink-0" aria-hidden />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
